import type {
  FacilityType,
  FleetMission,
  NotificationCategory,
  ShipCounts,
  ShipType,
  SystemType,
} from '@stars-and-ships/api-types';

/** Inbox tabs (G-80). */
export const NOTIFICATION_TAB_LABELS: Record<NotificationCategory, string> = {
  combat: 'Combat',
  economy: 'Economy',
  intel: 'Intel',
  alliance: 'Alliance',
  account: 'Account',
};

/** Names of the notice types in the inbox settings (G-82). */
export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  FLEET_INCOMING: 'Hostile fleet incoming',
  FLEET_INCOMING_CANCELLED: 'Hostile fleet turned back',
  FLEET_ORDER_FAILED: 'Timed fleet did not leave',
  FLEET_ORDER_RESCHEDULED: 'Timed fleet rescheduled',
  BATTLE_STARTED: 'Battle started',
  BATTLE_ENDED: 'Battle report (attacks and raids)',
  CLAIM_PLACED: 'Claim on your system',
  CLAIM_ACTIVATED: 'Claim activated',
  CLAIM_RESOLVED: 'Claim ended (captured, refunded or expired)',
  SYSTEM_OWNER_CHANGED: 'System lost',
  BUILDING_COMPLETED: 'Building upgrade finished',
  SHIP_PRODUCED: 'Ships built',
  PORT_TRADE_COMPLETED: 'Trade complete',
  SCOUT_RETURNED: 'Scout report',
  SCOUT_FAILED_AGAINST_YOU: 'Enemy scout failed against you',
  ALLIANCE_INVITE_RECEIVED: 'Alliance invite',
  ALLIANCE_APPLICATION_RECEIVED: 'New application to your alliance',
  ALLIANCE_APPLICATION_ACCEPTED: 'Your application accepted',
  ALLIANCE_RANK_CHANGED: 'Rank changed',
  ALLIANCE_KICKED: 'Removed from the alliance',
  ALLIANCE_DISBANDED: 'Alliance disbanded',
  SHIELD_WARNING: 'Beginner shield ending soon',
  SHIELD_EXPIRED: 'Beginner shield ended',
};

export const FACILITY_LABELS: Record<FacilityType, string> = {
  command_center: 'Command Center',
  metal_mine: 'Metal Mine',
  gas_harvester: 'Gas Harvester',
  supply_farm: 'Supply Farm',
  metal_storage: 'Metal Storage',
  gas_storage: 'Gas Storage',
  supply_storage: 'Supply Storage',
  trading_port: 'Trading Port',
  space_dock: 'Space Dock',
  habitat: 'Habitat',
  odp: 'Orbital Defense Platform',
  warp_gate: 'Warp Gate',
  observatory: 'Observatory',
  space_guild: 'Space Guild',
  research_center: 'Research Center',
  diplomatic_embassy: 'Diplomatic Embassy',
};

/** Buildings that are not part of the first playable slice (S-1). */
export const LATER_FACILITIES = new Set<FacilityType>([
  'observatory',
  'research_center',
  'space_guild',
]);

export const FACILITY_ORDER = Object.keys(FACILITY_LABELS) as FacilityType[];

export const SHIP_LABELS: Record<ShipType, string> = {
  interceptor: 'Interceptor',
  transport: 'Transport',
  frigate: 'Frigate',
  cruiser: 'Cruiser',
  carrier: 'Carrier',
  battleship: 'Battleship',
  dreadnought: 'Dreadnought',
};

export const SHIP_ORDER = Object.keys(SHIP_LABELS) as ShipType[];

export const MISSION_LABELS: Record<FleetMission, string> = {
  attack: 'Attack',
  raid: 'Raid',
  reinforce: 'Reinforce',
  transfer: 'Transfer',
  mission: 'Mission',
};

export const FLEET_STATUS_LABELS: Record<string, string> = {
  stationed: 'Stationed',
  in_transit: 'In flight',
  returning: 'Returning',
  waiting: 'Waiting to attack',
  in_battle: 'In battle',
  on_mission: 'On a mission',
};

export const END_REASON_LABELS: Record<string, string> = {
  defender_eliminated: 'every defending ship was destroyed',
  attacker_eliminated: 'every attacking ship was destroyed',
  mutual_destruction: 'both sides were destroyed',
  attacker_retreated: 'the attackers retreated',
  no_defenders: 'no ships defended the system',
};

/** "40 Interceptors · 5 Transports" (classes in combat order). */
export function formatShips(counts: ShipCounts): string {
  const parts = SHIP_ORDER.flatMap((type) => {
    const n = counts[type] ?? 0;
    return n > 0 ? [`${formatNumber(n)} ${SHIP_LABELS[type]}${n === 1 ? '' : 's'}`] : [];
  });
  return parts.length > 0 ? parts.join(' · ') : 'no ships';
}

export const SYSTEM_TYPE_LABELS: Record<SystemType, string> = {
  standard: 'Standard',
  resource_rich: 'Resource-Rich',
  strategic: 'Strategic',
  barren: 'Barren',
};

const numberFormat = new Intl.NumberFormat('en');
export const formatNumber = (value: number): string => numberFormat.format(value);

export const RESOURCE_KEYS = ['metal', 'gas', 'supplies'] as const;
export const RESOURCE_LABELS = { metal: 'Metal', gas: 'Gas', supplies: 'Supplies' } as const;

/** "45s", "2m 05s", "1h 05m", "2d 3h". */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const d = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${pad(m)}m` : `${h}h`;
  if (m > 0) return `${m}m ${pad(s)}s`;
  return `${s}s`;
}
